import {
  createClient,
  getRequestFactory,
  postRequestFactory,
  putRequestFactory,
  searchRequestFactory
} from '@infuse/api/common';

export interface CreateSmartChargingNetworkCommand {
  maxCurrent: number;
  minCurrent: number;
  name: string;
  chargingStationIds: string[];
}

export interface UpdateSmartChargingNetworkCommand {
  maxCurrent: number;
  minCurrent: number;
  name: string;
  chargingStationIds: string[];
}

export interface SmartChargingNetwork {
  maxCurrent: number;
  minCurrent: number;
  name: string;
  id: number;
  stage: string;
  totalEvses: number;
  enabled: boolean;
  chargingStationIds: string[];
}

export interface DetailedSmartChargingNetwork {
  chargingStations: SmartChargingStation[];
  state: SmartChargingNetworkState;
}

export interface SmartChargingStation {
  chargingStationId: string;
  maxCurrent?: number;
  currentSetpoint?: number;
  nextSetpoint?: number;
}

export enum SmartChargingNetworkState {
  DISABLED,
  IDLE,
  PENDING_RECALCULATION,
  RECALCULATION_IN_PROGRESS,
  RECALCULATION_IN_PROGRESS_WITH_PENDING_RECALCULATION
}

const smartChargingNetworkClient = createClient('/rest/operator-api/smart-charging-network');

export const smartChargingNetworkPermissions = {
  SEARCH: 'cpms.search-smart-charging-networks',
  GET: 'cpms.get-smart-charging-network',
  GET_DETAILED: 'cpms.get-smart-charging-network-detailed-state',
  CREATE: 'cpms.create-smart-charging-network',
  UPDATE: 'cpms.update-smart-charging-network',
  ENABLE_LOAD_BALANCING: 'cpms.enable-smart-charging-network-load-balancing',
  DISABLE_LOAD_BALANCING: 'cpms.disable-smart-charging-network-load-balancing',
  RECALCULATE_NETWORK: 'cpms.recalculate-smart-charging-network-currents'
};

export const searchSmartChargingNetwork = searchRequestFactory<SmartChargingNetwork>(smartChargingNetworkClient, {
  permissionId: smartChargingNetworkPermissions.SEARCH
});
export const getSmartChargingNetwork = getRequestFactory<SmartChargingNetwork, string>(smartChargingNetworkClient, {
  permissionId: smartChargingNetworkPermissions.GET
});
export const getDetailedSmartChargingNetwork = getRequestFactory<DetailedSmartChargingNetwork, string>(
  smartChargingNetworkClient,
  { pattern: '/{smartChargingNetworkId}/detailed-state', permissionId: smartChargingNetworkPermissions.GET_DETAILED }
);
export const createSmartChargingNetwork = postRequestFactory<CreateSmartChargingNetworkCommand, void>(
  smartChargingNetworkClient,
  { pattern: '', permissionId: smartChargingNetworkPermissions.CREATE }
);
export const updateSmartChargingNetwork = putRequestFactory<UpdateSmartChargingNetworkCommand, void>(
  smartChargingNetworkClient,
  { pattern: '/{smartChargingNetworkId}', permissionId: smartChargingNetworkPermissions.UPDATE }
);
export const enableLoadBalancing = postRequestFactory<void, string>(smartChargingNetworkClient, {
  pattern: '/{smartChargingNetworkId}/enable-load-balancing',
  permissionId: smartChargingNetworkPermissions.ENABLE_LOAD_BALANCING
});
export const disableLoadBalancing = postRequestFactory<void, string>(smartChargingNetworkClient, {
  pattern: '/{smartChargingNetworkId}/disable-load-balancing',
  permissionId: smartChargingNetworkPermissions.DISABLE_LOAD_BALANCING
});
export const recalculateNetwork = postRequestFactory<void, string>(smartChargingNetworkClient, {
  pattern: '/{smartChargingNetworkId}/recalculate',
  permissionId: smartChargingNetworkPermissions.RECALCULATE_NETWORK
});
